import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "../page.css";
import "../home.css";
import "./kilimanjaro.css";
import "./ol-dionyo-lengai.css";

const features = [
  {
    title: "Mountain of God",
    text: "Ol Doinyo Lengai is sacred to the Maasai. The climb is short on paper, but the ash slopes demand focus and strong legs.",
  },
  {
    title: "Active volcanic ground",
    text: "It is the only active volcano known for natrocarbonatite lava. The crater can change, so we check conditions before each ascent.",
  },
  {
    title: "Night to first light",
    text: "Most climbs start before midnight. Cooler ash underfoot, then sunrise over Lake Natron and the Rift Valley.",
  },
  {
    title: "Northern Tanzania pairing",
    text: "Often combined with Natron, Engare Sero, or a safari finish. We shape the days around heat, road time, and rest.",
  },
];

const routes = [
  {
    id: "night-ascent",
    name: "Night Ascent",
    days: "1 day, night climb",
    difficulty: "Challenging",
    lodging: "Camp or lodge near Natron",
    startGate: "Western ash slopes",
    bestFor: "Dawn summit and full crater approach",
    image:
      "https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Dramatic Rift Valley landscape under open sky",
    summary:
      "The classic Lengai experience. You leave camp late at night, climb steep ash and scree in the dark, and aim to stand on the rim as the sky turns.",
    body: "This is a hard, continuous effort rather than a multi day trek. Guides set a steady pace, and the surface can slide underfoot, especially near the top. Descent in daylight is faster but tough on knees. We only run the night ascent when local advice and recent crater reports look safe.",
    highlights: [
      "Midnight start to beat daytime heat on the ash",
      "Summit window timed for first light when possible",
      "Views across Lake Natron and the Rift escarpment",
      "Return to camp for a late breakfast and rest",
    ],
    itinerary: [
      { day: "Afternoon", text: "Arrive near Lake Natron, brief, early dinner, rest before the climb." },
      { day: "Night", text: "Depart with headlamps, climb the western slopes toward the crater." },
      { day: "Dawn", text: "Rim and crater views at first light, then careful descent." },
      { day: "Morning", text: "Back at camp for food, wash, and optional Natron time." },
    ],
    notes:
      "Bring gaiters or high socks for ash, a strong headlamp, and more water than you expect. We cancel or delay when the crater is unstable.",
  },
  {
    id: "crater-rim",
    name: "Crater Rim Trek",
    days: "1 day",
    difficulty: "Moderate to challenging",
    lodging: "Camp or lodge near Natron",
    startGate: "Western ash slopes",
    bestFor: "Rim views without a full night push",
    image:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open volcanic slopes under clear mountain light",
    summary:
      "A daylight or early morning climb focused on reaching the crater rim, with more flexibility on timing when a full midnight start is not the right fit.",
    body: "The Crater Rim Trek still uses the ash slopes and needs good fitness, but the schedule can start earlier in the evening or very early morning depending on heat and group strength. It suits travelers who want Lengai’s landscape and rim views, and who prefer not to move through the whole night. Summit conditions and turnaround times stay under guide control.",
    highlights: [
      "Same sacred mountain, more flexible start time",
      "Strong option in hotter seasons when nights are kinder",
      "Rim focus with clear safety turnaround points",
      "Pairs well with a Natron waterfall or flamingo day",
    ],
    itinerary: [
      { day: "Morning or evening", text: "Briefing at camp, then start while temperatures allow." },
      { day: "Ascent", text: "Steady climb on ash and scree toward the rim." },
      { day: "Rim", text: "Time on the crater edge as conditions allow, photos, short rest." },
      { day: "Descent", text: "Return to camp with poles recommended for the soft ash." },
    ],
    notes:
      "Ask for this plan if the group includes mixed fitness levels, or if you want Lengai without a full midnight to dawn schedule.",
  },
];

const practical = [
  {
    title: "Best months",
    text: "June to October, and the clearer windows in January and February. Soft ash is harder after heavy rain, and heat builds fast near Natron.",
  },
  {
    title: "Fitness",
    text: "This is a steep, loose climb. Prior hill walks help. Expect sliding ash, limited shade, and a descent that works the knees.",
  },
  {
    title: "What we arrange",
    text: "Local guides, transfers from Arusha or the northern circuit, camp or lodge nights near Natron, meals around the climb, and a clear packing list.",
  },
];

function OlDionyoLengai() {
  const { hash } = useLocation();

  useEffect(() => {
    document.title = "Ol Doinyo Lengai · Mirrorkhan Tours";
  }, []);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [hash]);

  return (
    <div className="kili-page lengai-page">
      <section className="kili-hero lengai-hero">
        <div className="kili-hero__media" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1800&q=80"
            alt=""
          />
        </div>
        <div className="kili-hero__shade" aria-hidden="true" />
        <div className="container kili-hero__content">
          <div className="row">
            <div className="col-lg-8 col-xl-7 mk-hero-enter">
              <p className="eyebrow eyebrow--light mb-3">
                <Link to="/trekking" className="text-decoration-none">
                  Trekking
                </Link>
              </p>
              <h1 className="mb-3">Ol Doinyo Lengai</h1>
              <p className="kili-hero__lede mb-4">
                The Mountain of God above Lake Natron. A steep ash climb, often
                by night, with crater light at dawn and the Rift Valley below.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a className="btn btn-mk-gold" href="#routes">
                  View routes
                </a>
                <Link className="btn btn-mk-ghost" to="/contact">
                  Ask about Lengai
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal>
            <div className="col-lg-8">
              <p className="eyebrow">The mountain</p>
              <h2 className="mb-3">Why Lengai feels unlike the other peaks</h2>
              <p className="page-lead mb-0">
                It is not a hut to hut trek. It is a focused volcanic ascent
                where surface, heat, and crater safety matter as much as
                fitness.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {features.map((item, index) => (
              <div
                className="col-md-6"
                key={item.title}
                data-reveal
                style={{ "--reveal-delay": `${(index % 2) * 100}ms` }}
              >
                <article className="kili-feature h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="routes" className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal>
            <div className="col-lg-8">
              <p className="eyebrow">Routes</p>
              <h2 className="mb-3">Choose the night push or a rim focused day</h2>
              <p className="page-lead mb-0">
                Both plans climb the ash slopes of Ol Doinyo Lengai. The
                difference is timing, how long you move in the dark, and how
                hard you press for the rim.
              </p>
            </div>
          </div>

          <div className="kili-routes">
            {routes.map((route, index) => (
              <article
                className={
                  index % 2 === 1
                    ? "kili-route kili-route--flip"
                    : "kili-route"
                }
                key={route.id}
                id={route.id}
                data-reveal={index % 2 === 1 ? "right" : "left"}
              >
                <div className="kili-route__top">
                  <div className="kili-route__media">
                    <img src={route.image} alt={route.imageAlt} />
                  </div>
                  <div className="kili-route__intro">
                    <p className="kili-route__meta mb-2">
                      {route.days}
                      <span aria-hidden="true"> · </span>
                      {route.difficulty}
                    </p>
                    <h3 className="h3 mb-3">{route.name}</h3>
                    <p className="kili-route__summary">{route.summary}</p>
                    <p className="kili-route__body-copy mb-0">{route.body}</p>
                  </div>
                </div>

                <div className="kili-route__panel">
                  <dl className="kili-route__facts">
                    <div>
                      <dt>Best for</dt>
                      <dd>{route.bestFor}</dd>
                    </div>
                    <div>
                      <dt>Start</dt>
                      <dd>{route.startGate}</dd>
                    </div>
                    <div>
                      <dt>Lodging</dt>
                      <dd>{route.lodging}</dd>
                    </div>
                    <div>
                      <dt>Duration</dt>
                      <dd>{route.days}</dd>
                    </div>
                  </dl>

                  <div className="row g-4">
                    <div className="col-lg-5">
                      <h4 className="kili-route__subhead">On the trail</h4>
                      <ul className="trek-points mb-0">
                        {route.highlights.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="col-lg-7">
                      <h4 className="kili-route__subhead">Sample outline</h4>
                      <ol className="kili-itinerary list-unstyled mb-0">
                        {route.itinerary.map((step) => (
                          <li key={step.day}>
                            <strong>{step.day}</strong>
                            <span>{step.text}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>

                  <p className="kili-route__note mb-3">{route.notes}</p>
                  <Link className="text-link" to="/contact">
                    Ask about {route.name}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal>
            <div className="col-lg-8">
              <p className="eyebrow">Before you climb</p>
              <h2 className="mb-0">Practical notes from the desk</h2>
            </div>
          </div>

          <div className="row g-4">
            {practical.map((item, index) => (
              <div
                className="col-md-4"
                key={item.title}
                data-reveal
                style={{ "--reveal-delay": `${index * 90}ms` }}
              >
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-close" data-reveal="fade">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <p className="eyebrow eyebrow--light">Plan your climb</p>
              <h2 className="mb-3">Tell us your dates and whether you want the night ascent.</h2>
              <p className="mb-0 home-close__copy">
                We reply with current crater advice, a clear outline, and a
                price that includes Natron logistics.
              </p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link className="btn btn-mk-gold" to="/contact">
                Contact the desk
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default OlDionyoLengai;
